import { Alert } from "@/src/lib/alert";
import { Ionicons } from "@/src/components/ui/ionicons";
import { Image } from "expo-image";
import { router } from "expo-router";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { useMemo, useState } from "react";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";

import { BrandIntro } from "@/src/components/brand/BrandIntro";
import { AuthField } from "@/src/components/ui/AuthField";
import { PrimaryButton } from "@/src/components/ui/PrimaryButton";
import { ScreenContainer } from "@/src/components/ui/ScreenContainer";
import { auth, db } from "@/src/lib/firebase";
import { getMyAccounts } from "@/src/lib/accounts";
import {
  avatarPhotoURL,
  isPresetAvatarPhotoURL,
  type NeqrawDefaultAvatarId,
} from "@/src/lib/avatarPresets";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { upsertPublicProfile } from "@/src/lib/publicProfiles";
import { SUBJECTS, TRACKS, type Subject, type Track } from "@/src/lib/taxonomy";
import type { LegacyAccountType } from "@/src/types/identity";
import { isValidUsername, normalizeUsername, setUniqueUsername } from "@/src/lib/usernames";
import { neqrawDefaultAvatars } from "@/src/theme/defaultAvatars";
import { border, brand, radius, spacing, surface, text, typography } from "@/src/theme/tokens";

type Role = LegacyAccountType;

function Choice({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="radio"
      accessibilityState={{ checked: active, selected: active }}
      aria-checked={Platform.OS === "web" ? active : undefined}
      onPress={onPress}
      style={({ pressed }) => [styles.choice, active && styles.choiceActive, pressed && styles.choicePressed]}
    >
      <Text style={[styles.choiceText, active && styles.choiceTextActive]}>{label}</Text>
      {active ? <Ionicons accessible={false} color={text.brand} name="checkmark-circle" size={18} /> : null}
    </Pressable>
  );
}

function AvatarChoice({
  avatar,
  active,
  onPress,
  index,
}: {
  avatar: (typeof neqrawDefaultAvatars)[number];
  active: boolean;
  onPress: () => void;
  index: number;
}) {
  return (
    <Pressable
      accessibilityLabel={`Avatar ${index + 1}`}
      accessibilityRole="radio"
      accessibilityState={{ checked: active, selected: active }}
      aria-checked={Platform.OS === "web" ? active : undefined}
      onPress={onPress}
      style={({ pressed }) => [
        styles.avatarChoice,
        active && styles.avatarChoiceActive,
        pressed && styles.choicePressed,
      ]}
    >
      <Image source={avatar.source} contentFit="cover" style={styles.avatarChoiceImage} />
      {active ? (
        <View style={styles.avatarCheck}>
          <Ionicons accessible={false} color={surface.primary} name="checkmark" size={14} />
        </View>
      ) : null}
    </Pressable>
  );
}

export default function OnboardingScreen() {
  const user = auth.currentUser;
  const [role, setRole] = useState<Role>("student");
  const [track, setTrack] = useState<Track | null>(TRACKS[0]?.value ?? null);
  const [mainSubject, setMainSubject] = useState<Subject | null>(
    SUBJECTS.find((subject) => subject.value !== "general")?.value ?? null
  );
  const [avatarId, setAvatarId] = useState<NeqrawDefaultAvatarId | null>(null);
  const [usernameDraft, setUsernameDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const usernameHint = useMemo(() => {
    const raw = usernameDraft.trim();
    const withoutAt = raw.startsWith("@") ? raw.slice(1) : raw;
    const username = normalizeUsername(withoutAt);
    if (!raw) return "Exemple : @mohamed_07";
    if (!isValidUsername(username)) return "3 à 16 caractères : a-z, 0-9 et _";
    return `Disponible à vérifier : @${username}`;
  }, [usernameDraft]);

  function onSelectRole(next: Role) {
    setRole(next);
    if (next === "student") setMainSubject(null);
    else setTrack(null);
  }

  async function onFinish() {
    if (!user) {
      Alert.alert("Erreur", "Tu n’es pas connecté.");
      router.replace("/(auth)/login");
      return;
    }

    const raw = usernameDraft.trim();
    const withoutAt = raw.startsWith("@") ? raw.slice(1) : raw;
    const nextUsername = normalizeUsername(withoutAt);
    if (!isValidUsername(nextUsername)) {
      Alert.alert("Erreur", "Nom d’utilisateur invalide. Utilise 3 à 16 caractères : a-z, 0-9, _");
      return;
    }
    if (role === "student" && !track) {
      Alert.alert("Erreur", "Choisis une filière.");
      return;
    }
    if (role === "teacher" && !mainSubject) {
      Alert.alert("Erreur", "Choisis une matière principale.");
      return;
    }
    const nextPhotoURL = avatarId ? avatarPhotoURL(avatarId) : "";
    if (!isPresetAvatarPhotoURL(nextPhotoURL)) {
      Alert.alert("Erreur", "Choisis un avatar.");
      return;
    }

    try {
      setBusy(true);
      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      const oldUsername = snap.exists() ? String((snap.data() as any)?.username ?? "") : "";
      if (!snap.exists()) {
        await setDoc(ref, {
          uid: user.uid,
          email: user.email ?? "",
          role: "",
          accountType: "",
          displayName: "",
          username: "",
          photoURL: "",
          teacherVerified: false,
          verificationStatus: "unverified",
          track: "",
          mainSubject: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      const patch: any = {
        role,
        accountType: role,
        displayName: nextUsername,
        photoURL: nextPhotoURL,
        teacherVerified: false,
        verificationStatus: "unverified",
        updatedAt: serverTimestamp(),
      };

      if (role === "student") {
        patch.track = track;
        patch.mainSubject = "";
      } else {
        patch.mainSubject = mainSubject;
        patch.track = "";
      }

      await setDoc(ref, patch, { merge: true });

      await setUniqueUsername({
        uid: user.uid,
        newUsername: nextUsername,
        oldUsername: oldUsername || null,
      });

      await upsertPublicProfile(
        user.uid,
        {
          username: nextUsername,
          displayName: nextUsername,
          role,
          accountType: role,
          verificationStatus: "unverified",
          track: role === "student" ? track ?? "" : "",
          mainSubject: role === "teacher" ? mainSubject ?? "" : "",
          photoURL: nextPhotoURL,
          createdAt: snap.exists() ? (snap.data() as any)?.createdAt : undefined,
        },
        { includeCreatedAt: true }
      );

      const accounts = await getMyAccounts();
      const personalAccount = accounts.find(
        (account) => account.isPersonal && account.accountId === user.uid
      );
      if (!personalAccount || personalAccount.photoURL !== nextPhotoURL) {
        throw new Error("L’identité personnelle n’a pas pu être créée avec cet avatar.");
      }

      await setDoc(ref, {
        onboardingCompletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (error: any) {
      Alert.alert(
        "Impossible d’enregistrer le profil",
        friendlyFirebaseError(error, "Réessaie dans un instant.")
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ScreenContainer contentStyle={styles.content}>
      <View style={styles.top}>
        <BrandIntro compact />
        <View style={styles.progressHeader}>
          <Text style={styles.step}>ÉTAPE 1 SUR 1</Text>
          <View style={styles.progressTrack}><View style={styles.progressValue} /></View>
        </View>
        <View style={styles.headingGroup}>
          <Text style={styles.heading}>Complète ton profil</Text>
          <Text style={styles.body}>Quelques repères pour te connecter aux bonnes personnes et aux bons contenus.</Text>
        </View>
      </View>

      <View style={styles.form}>
        <AuthField
          autoCapitalize="none"
          autoCorrect={false}
          hint={usernameHint}
          label="Nom d’utilisateur"
          onChangeText={setUsernameDraft}
          placeholder="@ton_username"
          value={usernameDraft}
        />

        <View accessibilityRole="radiogroup" style={styles.section}>
          <Text style={styles.sectionTitle}>Ton rôle</Text>
          <View style={styles.choices}>
            <Choice label="Élève" active={role === "student"} onPress={() => onSelectRole("student")} />
            <Choice label="Professeur" active={role === "teacher"} onPress={() => onSelectRole("teacher")} />
          </View>
        </View>

        <View accessibilityRole="radiogroup" style={styles.section}>
          <Text style={styles.sectionTitle}>{role === "student" ? "Ta filière" : "Ta matière principale"}</Text>
          <View style={styles.choices}>
            {role === "student"
              ? TRACKS.map((item) => (
                  <Choice key={item.value} label={item.label} active={track === item.value} onPress={() => setTrack(item.value)} />
                ))
              : SUBJECTS.filter((subject) => subject.value !== "general").map((item) => (
                  <Choice key={item.value} label={item.label} active={mainSubject === item.value} onPress={() => setMainSubject(item.value)} />
                ))}
          </View>
        </View>

        <View accessibilityRole="radiogroup" style={styles.section}>
          <Text style={styles.sectionTitle}>Ton avatar</Text>
          <View style={styles.avatarPreview}>
            {avatarId ? (
              <Image
                accessibilityLabel="Aperçu de l’avatar sélectionné"
                contentFit="cover"
                source={neqrawDefaultAvatars.find((avatar) => avatar.id === avatarId)?.source}
                style={styles.avatarPreviewImage}
              />
            ) : (
              <Ionicons accessible={false} color={text.muted} name="person-outline" size={46} />
            )}
          </View>
          <View style={styles.avatarChoices}>
            {neqrawDefaultAvatars.map((avatar, index) => (
              <AvatarChoice
                key={avatar.id}
                avatar={avatar}
                active={avatarId === avatar.id}
                index={index}
                onPress={() => setAvatarId(avatar.id)}
              />
            ))}
          </View>
        </View>

        <PrimaryButton busy={busy} busyLabel="Enregistrement…" label="Continuer" onPress={() => void onFinish()} />
      </View>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  content: { gap: spacing.xxl },
  top: { gap: spacing.xl },
  progressHeader: { gap: spacing.sm },
  step: { color: text.secondary, fontFamily: typography.family.semibold, fontSize: 12, letterSpacing: 0.8, lineHeight: 18 },
  progressTrack: { backgroundColor: brand.softGray, borderRadius: radius.pill, height: 4, overflow: "hidden" },
  progressValue: { backgroundColor: brand.primary, height: 4, width: "100%" },
  headingGroup: { gap: spacing.sm },
  heading: { color: text.primary, fontFamily: typography.family.semibold, fontSize: typography.size.heading, lineHeight: typography.lineHeight.heading },
  body: { color: text.secondary, fontFamily: typography.family.regular, fontSize: typography.size.body, lineHeight: typography.lineHeight.body, maxWidth: 520 },
  form: { gap: spacing.xl },
  section: { gap: spacing.md },
  sectionTitle: { color: text.primary, fontFamily: typography.family.semibold, fontSize: typography.size.body, lineHeight: typography.lineHeight.body },
  choices: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  choice: {
    alignItems: "center",
    backgroundColor: surface.primary,
    borderColor: border.subtle,
    borderRadius: radius.md,
    borderWidth: 1,
    flexDirection: "row",
    gap: spacing.sm,
    minHeight: 48,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  choiceActive: { backgroundColor: surface.selected, borderColor: border.brand, borderWidth: 2, paddingHorizontal: spacing.lg - 1, paddingVertical: spacing.md - 1 },
  choicePressed: { opacity: 0.8 },
  choiceText: { color: text.primary, fontFamily: typography.family.regular, fontSize: 15, lineHeight: 21 },
  choiceTextActive: { color: text.brand, fontFamily: typography.family.semibold },
  avatarPreview: {
    alignItems: "center",
    alignSelf: "center",
    backgroundColor: surface.overlay,
    borderColor: border.subtle,
    borderRadius: radius.pill,
    borderStyle: "dashed",
    borderWidth: 1,
    height: 112,
    justifyContent: "center",
    overflow: "hidden",
    width: 112,
  },
  avatarPreviewImage: { height: "100%", width: "100%" },
  avatarChoices: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
    justifyContent: "center",
  },
  avatarChoice: {
    backgroundColor: surface.primary,
    borderColor: border.subtle,
    borderRadius: radius.pill,
    borderWidth: 2,
    height: 76,
    padding: 3,
    position: "relative",
    width: 76,
  },
  avatarChoiceActive: { backgroundColor: surface.selected, borderColor: border.brand },
  avatarChoiceImage: { borderRadius: radius.pill, height: "100%", width: "100%" },
  avatarCheck: {
    alignItems: "center",
    backgroundColor: brand.primary,
    borderColor: surface.primary,
    borderRadius: radius.pill,
    borderWidth: 2,
    bottom: -2,
    height: 24,
    justifyContent: "center",
    position: "absolute",
    right: -2,
    width: 24,
  },
});
